/**
 * Status Toggle Dialog Component
 * 
 * Reusable confirmation dialog for status toggle operations.
 * Provides consistent confirmation messaging across modules.
 * 
 * @module StatusToggleDialog
 */

import React from 'react';
import { CustomDialog } from '@voicecare.ai/ui-library';
import { useTheme } from '../../ThemeContext';
import { TOGGLE_MESSAGES } from '../../constants/filter-constants';

/**
 * StatusToggleDialog Component
 * 
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether dialog is open
 * @param {Object} props.entity - Entity being toggled
 * @param {string} props.action - Action being performed ('activate' or 'deactivate')
 * @param {string} props.module - Module name ('facility', 'user', 'provider')
 * @param {Function} props.onConfirm - Confirm handler
 * @param {Function} props.onCancel - Cancel handler
 * @param {boolean} [props.loading=false] - Loading state
 * @returns {JSX.Element} StatusToggleDialog component
 * 
 * @example
 * <StatusToggleDialog
 *   open={isDialogOpen}
 *   entity={selectedEntity}
 *   action={toggleAction}
 *   module="facility"
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 *   loading={isLoading}
 * />
 */
const StatusToggleDialog = ({
  open,
  entity,
  action,
  module,
  onConfirm,
  onCancel,
  loading = false,
  ...otherProps
}) => {
  const { currentTheme, themeMode } = useTheme();

  /**
   * Gets the entity name for display
   * 
   * @returns {string} Entity name
   */
  const getEntityName = () => {
    if (!entity) return 'this item';
    
    return entity.facilityName || 
           entity.fullName || 
           entity.name || 
           'this item';
  };

  /**
   * Gets the confirmation message
   * 
   * @returns {string} Confirmation message
   */
  const getConfirmationMessage = () => {
    if (!entity || !action) {
      return `Are you sure you want to change the ${module} status?`;
    }

    const entityName = getEntityName();
    const moduleMessages = TOGGLE_MESSAGES[module] || TOGGLE_MESSAGES.facility;
    const actionMessage = moduleMessages[action] || '';
    
    return `Are you sure you want to ${action} "${entityName}"? ${actionMessage}`;
  };

  return (
    <CustomDialog
      open={open}
      message={getConfirmationMessage()}
      onConfirm={onConfirm}
      onCancel={onCancel}
      themeMode={themeMode}
      currentTheme={currentTheme}
      loading={loading}
      {...otherProps}
    />
  );
};

export default StatusToggleDialog;
